import { UserCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { organizationService } from "../../services/organizations";

interface OwnerSelectorProps {
  organizationId: string;
  selectedOwnerId?: string | null;
  onOwnerSelect: (ownerId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

interface OrganizationMember {
  id: string;
  user_id: string;
  user_email?: string; // API returns user_email
  user_name?: string; // API returns combined name as user_name
  email?: string; // Fallback for older API
  first_name?: string; // May not be provided
  last_name?: string; // May not be provided
  role: string;
}

export const OwnerSelector: React.FC<OwnerSelectorProps> = ({
  organizationId,
  selectedOwnerId,
  onOwnerSelect,
  placeholder = "Select owner (DRI)",
  disabled = false,
  className = "",
}) => {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, [organizationId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const membersList =
        await organizationService.getOrganizationMembers(organizationId);
      setMembers(membersList);
    } catch (err) {
      console.error("Failed to fetch organization members:", err);
    } finally {
      setLoading(false);
    }
  };

  const selectedMember = members.find((m) => m.user_id === selectedOwnerId);

  const getMemberDisplayName = (member: OrganizationMember): string => {
    if (!member) return "";

    // First, try the combined user_name from API
    if (member.user_name && member.user_name.trim()) {
      return member.user_name;
    }

    // Then try to use first/last name if available
    const fullName =
      `${member.first_name || ""} ${member.last_name || ""}`.trim();
    if (fullName) {
      return fullName;
    }

    // Fall back to email (try user_email first, then email)
    return member.user_email || member.email || "Unknown Member";
  };

  const getMemberInitials = (member: OrganizationMember): string => {
    if (!member) return "U";

    // Try user_name first (combined name from API)
    if (member.user_name && member.user_name.trim()) {
      const parts = member.user_name.trim().split(" ");
      if (parts.length >= 2) {
        // Use first letter of first and last parts
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      } else if (parts.length === 1 && parts[0].length > 0) {
        // Use first two letters of single name
        return parts[0].substring(0, 2).toUpperCase();
      }
    }

    // Try first and last name initials
    if (member.first_name && member.last_name) {
      return `${member.first_name[0]}${member.last_name[0]}`.toUpperCase();
    }

    // Try just first name
    if (member.first_name) {
      return member.first_name.substring(0, 2).toUpperCase();
    }

    // Try just last name
    if (member.last_name) {
      return member.last_name.substring(0, 2).toUpperCase();
    }

    // Fall back to email initials
    const email = member.user_email || member.email;
    if (email && email.includes("@")) {
      const emailPart = email.split("@")[0];
      return emailPart.substring(0, 2).toUpperCase();
    }

    return "U";
  };

  const filteredMembers = members.filter((member) => {
    const name = getMemberDisplayName(member);
    if (!name || !searchQuery) return true; // Show all if no search query
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className={`relative ${className}`}>
      <div
        className={`
          flex items-center justify-between
          w-full px-3 py-2
          border border-gray-300 rounded-md
          bg-white shadow-sm
          ${disabled ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}
          focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center flex-1">
          {selectedMember ? (
            <>
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-xs font-medium mr-2">
                {getMemberInitials(selectedMember)}
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {getMemberDisplayName(selectedMember)}
                </div>
                <div className="text-xs text-gray-500 capitalize">
                  {selectedMember.role}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center text-gray-400">
              <UserCircleIcon className="h-5 w-5 mr-2" />
              <span className="text-sm">{placeholder}</span>
            </div>
          )}
        </div>

        {selectedMember && !disabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOwnerSelect(null);
            }}
            className="ml-2 p-1 hover:bg-gray-100 rounded"
            type="button"
          >
            <XMarkIcon className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {loading ? (
            <div className="p-3 text-center text-sm text-gray-500">
              Loading members...
            </div>
          ) : (
            <div className="py-1">
              {/* Unassigned option */}
              <div
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                onClick={() => {
                  onOwnerSelect(null);
                  setIsOpen(false);
                  setSearchQuery("");
                }}
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-500 text-xs font-medium mr-2">
                  U
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-600">
                    Unassigned
                  </div>
                  <div className="text-xs text-gray-400">No owner</div>
                </div>
                {!selectedOwnerId && (
                  <svg
                    className="h-5 w-5 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>

              {/* Divider */}
              {filteredMembers.length > 0 && (
                <div className="border-t border-gray-200 my-1"></div>
              )}

              {/* Members list */}
              {filteredMembers.length === 0 ? (
                <div className="p-3 text-center text-sm text-gray-500">
                  No members found
                </div>
              ) : (
                filteredMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                    onClick={() => {
                      onOwnerSelect(member.user_id);
                      setIsOpen(false);
                      setSearchQuery("");
                    }}
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 text-xs font-medium mr-2">
                      {getMemberInitials(member)}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {getMemberDisplayName(member)}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {member.role}
                      </div>
                    </div>
                    {member.user_id === selectedOwnerId && (
                      <svg
                        className="h-5 w-5 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
